import superagent from 'superagent';
import { success, error } from '@redux-requests/core';
import {
  all,
  call,
  put,
  putResolve,
  race,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { toast } from 'react-toastify';

import { DefaultHeadersObject } from 'shared/utils/request-utils';
import { WORKFLOWS_PATH } from 'shared/constants/routePaths';
import { CREATED_WORKFLOW_MODAL, REMOVE_WORKFLOW_MODAL } from 'shared/constants/modalNames';

import history from '../../customHistory';

import { openModal, closeModal } from '../modal/actions';
import { FETCH_ENCLAVES } from '../enclave/actions';
import { fetchEnclaves } from '../enclave/actionCreators';
import { getApiCredentials, regenerateApiCredentials } from '../applicationUser/actions';

import * as actions from './actions';
import {
  handleWorkflowsFetch as handleWorkflowsFetchActionCreator,
  getWorkflowApiCredentials as getWorkflowApiCredentialsActionCreator,
  addApiCredentialsToWorkflow,
} from './actionCreators';
import {
  DeleteWorkflowAction,
  GetWorkflowApiCredentialsAction,
} from './types';

// Watcher sagas
function* watchWorkflowFetchActions() {
  yield takeLatest(actions.FETCH_WORKFLOWS, handleWorkflowsFetch);
}

function* watchWorkflowCreationAction() {
  yield takeLatest(actions.CREATE_WORKFLOW, createWorkflow);
}

function* watchWorkflowDeleteAction() {
  yield takeLatest(actions.DELETE_WORKFLOW, deleteWorkflow);
}

function* watchEditWorkflowAction() {
  yield takeLatest(actions.EDIT_WORKFLOW, editWorkflow);
}

function* watchGetWorkflowApiCredentialsAction() {
  yield takeEvery(actions.GET_WORKFLOW_API_CREDENTIALS, getWorkflowApiCredentials);
}

// Worker sagas
function* handleWorkflowsFetch() {
  const { successfulAction, failureAction } = yield race({
    successfulAction: take(success(actions.FETCH_WORKFLOWS)),
    failureAction: take(error(actions.FETCH_WORKFLOWS)),
  });

  if (successfulAction) {
    yield put(handleWorkflowsFetchActionCreator(successfulAction.payload.data));
  } else {
    const { message } = failureAction.payload;
    const errorMessage = message || 'An error occurred while fetching your workflows.';

    yield put(handleWorkflowsFetchActionCreator([]));
    yield call(toast.error, errorMessage);
  }
}

function* createWorkflow() {
  const { successfulAction, failureAction } = yield race({
    successfulAction: take(success(actions.CREATE_WORKFLOW)),
    failureAction: take(error(actions.CREATE_WORKFLOW)),
  });

  if (successfulAction) {
    const { data } = successfulAction.payload;

    yield put(fetchEnclaves());
    yield take(success(FETCH_ENCLAVES));

    yield put(getWorkflowApiCredentialsActionCreator(data.guid, data.destination.type));
    yield take(actions.ADD_API_CREDENTIALS_TO_WORKFLOW);

    yield put(openModal(CREATED_WORKFLOW_MODAL));
    yield call(history.push, WORKFLOWS_PATH);
  } else {
    const { message } = failureAction.payload;
    const errorMessage = message || 'An error occurred while creating the workflow.';

    yield call(toast.error, errorMessage);
  }
}

function* deleteWorkflow(action: DeleteWorkflowAction) {
  const { payload } = action;

  try {
    // TODO: Temporary! We had to do the request manually instead of using `redux-requests`
    // because there's a bug that breaks the app when the server returns nothing.
    yield superagent
      .delete(`/services/workflows/${payload.workflow.guid}`)
      .set(DefaultHeadersObject.get());
    yield put({ type: success(actions.DELETE_WORKFLOW), payload });
    yield put(closeModal(REMOVE_WORKFLOW_MODAL));
  } catch (e) {
    const errorMessage = e.message || 'An error occurred while deleting the workflow.';

    yield put({ type: error(actions.DELETE_WORKFLOW), payload });
    yield call(toast.error, errorMessage);
  }
}

function* editWorkflow() {
  const { successfulAction, failureAction } = yield race({
    successfulAction: take(success(actions.EDIT_WORKFLOW)),
    failureAction: take(error(actions.EDIT_WORKFLOW)),
  });

  if (successfulAction) {
    yield call(history.push, WORKFLOWS_PATH);
  } else {
    const { message } = failureAction.payload;
    const errorMessage = message || 'An error occurred while updating the workflow.';

    yield call(toast.error, errorMessage);
  }
}

function* getWorkflowApiCredentials(action: GetWorkflowApiCredentialsAction) {
  const { workflowGuid, destinationType, regenerate } = action.payload;

  try {
    const actionCreatorFn = regenerate ? regenerateApiCredentials : getApiCredentials;
    const { data, error } = yield putResolve(actionCreatorFn(destinationType));

    if (error) {
      throw new Error('An error occurred while fetching the API credentials.');
    }

    const { clientId, clientSecret } = data;
    yield put(addApiCredentialsToWorkflow(workflowGuid, {
      apiKey: clientId,
      apiSecret: clientSecret,
    }));

  } catch (e) {
    yield call(toast.error, e.message);
    yield put({
      type: error(actions.GET_WORKFLOW_API_CREDENTIALS),
      payload: { workflowGuid },
    });
  }
}

export default function* WorkflowSagas() {
  yield all([
    watchWorkflowFetchActions(),
    watchWorkflowCreationAction(),
    watchWorkflowDeleteAction(),
    watchEditWorkflowAction(),
    watchGetWorkflowApiCredentialsAction(),
  ]);
}
